import React, { useLayoutEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { megaLocations, megaSocialLinks, servicesMegaColumns } from "../../data/headerNav";
import { TextReveal } from "../NewButton";

/** Apple globalnav — cubic-bezier(0.4, 0, 0.6, 1) */
export const appleEase = [0.4, 0, 0.6, 1];

/** --r-globalnav-flyout-rate */
export const panelTransition = {
    open: { duration: 0.4, ease: appleEase },
    close: { duration: 0.4, ease: appleEase },
    morph: { duration: 0.4, ease: appleEase },
};

/** .globalnav-curtain: opacity .32s cubic-bezier(.4, 0, .6, 1) 80ms */
export const curtainTransition = {
    duration: 0.32,
    ease: appleEase,
    delay: 0.08,
};

const REVEAL_GREEN = "#417B5A";

/**
 * Apple submenu text:
 * opacity + translateY(-4px), 0.32s, delay = groupDelay + 80ms
 */
const textTransition = (groupDelay = 0) => ({
    duration: 0.32,
    ease: appleEase,
    delay: groupDelay + 0.08,
});

const FlyoutText = ({ children, groupDelay = 0, className = "" }) => (
    <motion.div
        className={className}
        initial={{ opacity: 0, y: -4 }}
        animate={{
            opacity: 1,
            y: 0,
            transition: textTransition(groupDelay),
        }}
    >
        {children}
    </motion.div>
);

const megaLinkClass =
    "tw:group tw:inline-flex tw:items-center tw:no-underline tw:leading-[1.2]";

const ServicesContent = ({ onNavigate, text, muted, border }) => (
    <div className="tw:px-8 tw:1xl:px-[100px] tw:pt-12 tw:md:pt-[60px] tw:pb-9 tw:md:pb-[48px]">
        <div className="tw:grid tw:grid-cols-1 tw:md:grid-cols-3 tw:gap-12 tw:md:gap-10 tw:2xl:gap-[100px]">
            {servicesMegaColumns.map((column, groupIndex) => {
                const groupDelay = groupIndex * 0.03;
                return (
                    <div key={column.title}>
                        <FlyoutText groupDelay={groupDelay}>
                            <p
                                className="tw:text-xl tw:font-medium tw:mb-6 tw:md:mb-8 tw:m-0"
                                style={{ color: muted }}
                            >
                                {column.title}
                            </p>
                        </FlyoutText>
                        <ul className="tw:list-none tw:p-0 tw:m-0 tw:flex tw:flex-col tw:gap-3.5 tw:md:gap-6">
                            {column.links.map((item, itemIndex) => (
                                <li key={item.label}>
                                    <FlyoutText groupDelay={groupDelay + itemIndex * 0.01}>
                                        <Link
                                            to={item.to}
                                            onClick={onNavigate}
                                            aria-label={item.label}
                                            className={`tw:text-lg tw:md:text-xl tw:xl:text-[32px] ${megaLinkClass}`}
                                            style={{ color: text }}
                                        >
                                            <TextReveal revealColor={REVEAL_GREEN}>{item.label}</TextReveal>
                                        </Link>
                                    </FlyoutText>
                                </li>
                            ))}
                        </ul>
                    </div>
                );
            })}
        </div>

        <FlyoutText groupDelay={0.12}>
            <div
                className="tw:mt-12 tw:md:mt-[60px] tw:pt-[48px] tw:border-t tw:flex tw:flex-col tw:sm:flex-row tw:items-start tw:sm:items-center tw:justify-between tw:gap-4"
                style={{ borderColor: border }}
            >
                <div className="tw:flex tw:items-center tw:gap-6">
                    {megaLocations.map((location) => (
                        <span
                            key={location.label}
                            className="tw:text-xl tw:leading-[1.2] tw:font-medium"
                            style={{ color: muted }}
                        >
                            {location.label}
                        </span>
                    ))}
                </div>
                <div className="tw:flex tw:items-center tw:gap-6">
                    {megaSocialLinks.map((social) => (
                        <a
                            key={social.id}
                            href={social.href}
                            target="_blank"
                            rel="noopener noreferrer"
                            aria-label={social.ariaLabel || social.label}
                            title={social.ariaLabel || social.label}
                            className={`tw:text-xl tw:font-medium ${megaLinkClass}`}
                            style={{ color: text }}
                        >
                            <TextReveal revealColor={REVEAL_GREEN}>{social.label}</TextReveal>
                        </a>
                    ))}
                </div>
            </div>
        </FlyoutText>
    </div>
);

/** Services-only flyout — matches header page theme */
const ServicesMegaMenu = ({ menu, onNavigate, theme }) => {
    const open = menu === "services";
    const measureRef = useRef(null);
    const [height, setHeight] = useState(0);

    const bg = theme?.bg || "#ffffff";
    const text = theme?.text || "#222";
    const border = theme?.border || "#2222221A";
    const muted = theme?.text ? `${theme.text}80` : "rgba(34, 34, 34, 0.5)";

    useLayoutEffect(() => {
        if (!open) return undefined;

        const node = measureRef.current;
        if (!node) return undefined;

        const update = () => setHeight(node.offsetHeight);
        update();

        const ro = typeof ResizeObserver !== "undefined" ? new ResizeObserver(update) : null;
        ro?.observe(node);

        return () => ro?.disconnect();
    }, [open]);

    return (
        <motion.div
            initial={false}
            animate={{ height: open ? height : 0 }}
            transition={open ? panelTransition.open : panelTransition.close}
            className="tw:absolute tw:left-0 tw:right-0 tw:top-full tw:z-50 tw:overflow-hidden"
            style={{
                willChange: "height",
                boxShadow: open ? `0 1px 0 0 ${border}` : "none",
                backgroundColor: bg,
                color: text,
            }}
            aria-hidden={!open}
        >
            {open ? (
                <div ref={measureRef}>
                    <ServicesContent
                        onNavigate={onNavigate}
                        text={text}
                        muted={muted}
                        border={border}
                    />
                </div>
            ) : null}
        </motion.div>
    );
};

export default ServicesMegaMenu;